import * as React from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { CUSTOMER_SEGMENTS, type ConsoleStats, type CustomerSegment, type CustomerStatus } from '@acs/shared'
import { apiGet } from '@/lib/api'

/**
 * The header strip's aggregate counts.
 *
 * The stream pushes a full `stats` object whenever the numbers move, so the
 * poll below is a safety net for a dropped stream rather than the refresh
 * mechanism — hence a minute, not a second. Key is `['stats']` exactly;
 * `useRealtime()` writes to that key.
 */
export function useStats(): UseQueryResult<ConsoleStats> {
  return useQuery({
    queryKey: ['stats'] as const,
    queryFn: ({ signal }) => apiGet<ConsoleStats>('/api/stats', undefined, signal),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

export interface NavCounts {
  /** Status counts as the NAVIGATION shows them — see the note below. */
  customers: Record<CustomerStatus, number> | undefined
  /** Pending count per segmented queue, for its own entry. */
  segments: Record<CustomerSegment, number> | undefined
}

/**
 * The counts the navigation shows, which are not quite the ones the API sends.
 *
 * `ConsoleStats.customers.pending` is EVERY pending customer, segmented queues
 * included — that is what the field means and it stays that way. The rail and
 * the topbar each show those queues as their own entries, so what they label
 * "Pending" is that number minus the ones that moved out.
 *
 * The subtraction is exact and cannot go negative: both numbers come from one
 * `$facet` over one stream in `getConsoleStats`, so a segment's bucket is a
 * strict subset of the status bucket at the same instant. The clamp guards one
 * thing only — a payload from an API older than `segments`.
 *
 * It lives here, in one selector, because there are two nav surfaces: a rail
 * and a topbar that disagree by a row are a bug nobody can attribute. Memoised
 * on the stats object's identity, so a re-render that does not change the data
 * hands both consumers the same references and neither re-renders.
 */
export function useNavCounts(): NavCounts {
  const { data } = useStats()

  return React.useMemo<NavCounts>(() => {
    if (!data) return { customers: undefined, segments: undefined }

    const segments = {} as Record<CustomerSegment, number>
    let claimed = 0
    for (const segment of CUSTOMER_SEGMENTS) {
      const pending = data.segments?.[segment]?.pending ?? 0
      segments[segment] = pending
      claimed += pending
    }

    return {
      customers: { ...data.customers, pending: Math.max(0, data.customers.pending - claimed) },
      segments,
    }
  }, [data])
}
